import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, MapPin, Clock, Briefcase, Calendar, IndianRupee } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getJobBySlug } from "@/lib/careers.functions";
import { ApplicationForm } from "@/components/careers/ApplicationForm";
import { format } from "date-fns";

export const Route = createFileRoute("/careers/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `Apply for ${params.slug.charAt(0).toUpperCase() + params.slug.slice(1)} | Careers` },
      { name: "description", content: `View details and apply for the ${params.slug} position at Poonam Kapur Healthy Kitchen.` },
      { property: "og:title", content: `Career Opportunity: ${params.slug.charAt(0).toUpperCase() + params.slug.slice(1)}` },
      { property: "og:description", content: "Join our mission-driven team in Mumbai. Apply now." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: JobDetailPage,
});

function JobDetailPage() {
  const { slug } = Route.useParams();
  const fetchJob = useServerFn(getJobBySlug);

  const { data: job, isLoading, error } = useQuery({
    queryKey: ["careers", "detail", slug],
    queryFn: () => fetchJob({ data: { slug } }),
  });

  if (isLoading) {
    return (
      <SiteLayout>
        <Section>
          <div className="flex items-center justify-center py-20">
            <p className="text-sm text-muted-foreground">Loading job details...</p>
          </div>
        </Section>
      </SiteLayout>
    );
  }

  if (error || !job) {
    throw notFound();
  }

  const postedDate = job.published_at 
    ? format(new Date(job.published_at), "PPP")
    : format(new Date(job.created_at), "PPP");

  const experienceLabel =
    job.experience_required ||
    (job.min_experience && job.max_experience
      ? `${job.min_experience}–${job.max_experience} years`
      : job.min_experience
        ? `${job.min_experience}+ years`
        : job.max_experience || null);


  return (
    <SiteLayout>
      <Container className="pt-16 md:pt-24">
        <Link
          to="/careers"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden strokeWidth={1.75} />
          Back to Careers
        </Link>

        <div className="mt-10 flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl leading-tight md:text-5xl lg:text-6xl">
              {job.title}
            </h1>
            
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-primary/60" />
                <span>{job.location}</span>
              </div>
              {job.employment_type && (
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary/60" />
                  <span>{job.employment_type}</span>
                </div>
              )}
              {experienceLabel && (
                <div className="flex items-center gap-1.5">
                  <Briefcase className="h-4 w-4 text-primary/60" />
                  <span>{experienceLabel}</span>
                </div>
              )}
              {job.salary_text && (
                <div className="flex items-center gap-1.5">
                  <IndianRupee className="h-4 w-4 text-primary/60" />
                  <span>{job.salary_text}</span>
                </div>
              )}

              {job.department && (
                <div className="flex items-center gap-1.5">
                  <Badge variant="secondary" className="font-normal">{job.department}</Badge>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-primary/60" />
                <span>Posted on {postedDate}</span>
              </div>
            </div>
          </div>

          <div className="shrink-0">
            <Button asChild className="rounded-full px-8 py-6 text-base" size="lg">
              <a href="#apply-now">Apply Now</a>
            </Button>
          </div>
        </div>
      </Container>

      <Section>
        <div className="grid gap-16 lg:grid-cols-[1fr_400px]">
          <div className="space-y-12">
            {/* Main Details */}
            <div className="prose prose-sm md:prose-base max-w-none text-foreground/85">
              {job.description && (
                <div className="space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Job Description</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.description}</div>
                </div>
              )}

              {job.responsibilities && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Key Responsibilities</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.responsibilities}</div>
                </div>
              )}

              {job.qualifications && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Required Qualifications</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.qualifications}</div>
                </div>
              )}

              {job.skills && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Required Skills</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.skills}</div>
                </div>
              )}

              {job.preferred_skills && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Preferred Skills</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.preferred_skills}</div>
                </div>
              )}

              {job.working_hours && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Working Hours</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.working_hours}</div>
                </div>
              )}

              {job.benefits && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Benefits & Perks</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.benefits}</div>
                </div>
              )}

              {job.additional_information && (
                <div className="mt-10 space-y-4">
                  <h3 className="font-display text-2xl font-medium text-foreground">Additional Information</h3>
                  <div className="whitespace-pre-wrap leading-relaxed">{job.additional_information}</div>
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-8">
            <div className="rounded-3xl border border-border bg-secondary/30 p-8">
              <h4 className="font-display text-xl font-medium">Quick Overview</h4>
              <dl className="mt-6 space-y-4">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">Location</dt>
                  <dd className="mt-1 text-sm font-medium">{job.location}</dd>
                </div>
                {job.employment_type && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Employment Type</dt>
                    <dd className="mt-1 text-sm font-medium">{job.employment_type}</dd>
                  </div>
                )}
                {job.department && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Department</dt>
                    <dd className="mt-1 text-sm font-medium">{job.department}</dd>
                  </div>
                )}
                {experienceLabel && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Experience</dt>
                    <dd className="mt-1 text-sm font-medium">{experienceLabel}</dd>
                  </div>
                )}
                {job.salary_text && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Salary / Package</dt>
                    <dd className="mt-1 text-sm font-medium">{job.salary_text}</dd>
                  </div>
                )}
                {job.working_hours && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Working Hours</dt>
                    <dd className="mt-1 text-sm font-medium">{job.working_hours}</dd>
                  </div>
                )}
                {job.application_deadline && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-muted-foreground">Application Deadline</dt>
                    <dd className="mt-1 text-sm font-medium">
                      {format(new Date(job.application_deadline), "PPP")}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            
            <div className="rounded-3xl border border-dashed border-primary/30 p-8">
              <h4 className="font-display text-xl font-medium">Any Questions?</h4>
              <p className="mt-3 text-sm text-muted-foreground">
                Feel free to reach out to us if you have any questions about this role or our hiring process.
              </p>
              <Button asChild variant="link" className="mt-4 h-auto p-0 text-primary">
                <Link to="/contact">Contact Support</Link>
              </Button>
            </div>
          </aside>
        </div>
      </Section>

      <Section id="apply-now" className="bg-secondary/10">
        <div className="mx-auto max-w-4xl">
          <ApplicationForm job={job} />
        </div>
      </Section>
    </SiteLayout>
  );
}
